/**
 * Push Notification Service
 * Web Push 알림 관리
 */

import api from './api';
import { getOrCreateDeviceId } from '@/utils/deviceId';

// VAPID public key will be fetched from the backend
let vapidPublicKey: string | null = null;

/**
 * Convert URL-safe base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check if notification permission is granted
 */
export function isNotificationPermissionGranted(): boolean {
  return Notification.permission === 'granted';
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.error('This browser does not support notifications');
    return 'denied';
  }

  return await Notification.requestPermission();
}

/**
 * Get VAPID public key from the backend
 */
async function getVapidPublicKey(): Promise<string> {
  if (vapidPublicKey) {
    return vapidPublicKey;
  }

  const response = await api.get('/notifications/push/vapid-public-key');
  vapidPublicKey = response.data.public_key;
  return vapidPublicKey!;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) {
    console.warn('Push notifications are not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

function sameServerKey(subscription: PushSubscription, expected: Uint8Array): boolean {
  const current = subscription.options?.applicationServerKey;
  if (!current) return true; // 알 수 없으면 유지
  const bytes = new Uint8Array(current);
  if (bytes.length !== expected.length) return false;
  for (let i = 0; i < bytes.length; i++) if (bytes[i] !== expected[i]) return false;
  return true;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(): Promise<boolean> {
  try {
    // Service Worker가 지원되지 않으면 조용히 종료
    if (!isPushSupported()) {
      console.log('Push notifications not supported, skipping subscribe');
      return false;
    }

    // Request permission first
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key
    const publicKey = await getVapidPublicKey();

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();
    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    // 서버 VAPID 키가 바뀐 뒤 남아 있는 옛 구독은 발송이 전부 실패(403)하므로 다시 만든다
    if (subscription && !sameServerKey(subscription, applicationServerKey)) {
      try {
        await subscription.unsubscribe();
      } catch {
        // 이미 해지됨
      }
      subscription = null;
    }

    if (!subscription) {
      // Create new subscription
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
      });
    }

    // Extract keys from subscription
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');

    if (!p256dhKey || !authKey) {
      console.error('Failed to get subscription keys');
      return false;
    }

    // Convert keys to base64
    const p256dhBase64 = btoa(String.fromCharCode(...new Uint8Array(p256dhKey)));
    const authBase64 = btoa(String.fromCharCode(...new Uint8Array(authKey)));

    // Send subscription to backend
    await api.post('/notifications/push/subscribe', {
      endpoint: subscription.endpoint,
      p256dh_key: p256dhBase64,
      auth_key: authBase64,
      user_agent: navigator.userAgent,
      device_id: getOrCreateDeviceId(),
    });

    console.log('Push subscription successful');
    return true;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    // Service Worker가 지원되지 않으면 조용히 종료
    if (!isPushSupported()) {
      console.log('Push notifications not supported, skipping unsubscribe');
      return true;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Notify backend
      await api.delete('/notifications/push/unsubscribe', {
        params: { endpoint: subscription.endpoint },
      });
    }

    console.log('Push unsubscription successful');
    return true;
  } catch (error) {
    console.error('Push unsubscription failed:', error);
    return false;
  }
}

/**
 * Get current push subscription status
 *
 * `browserSubscribed`: 브라우저 PushManager 에 구독 객체가 있는지
 * `serverActive`: 서버에 활성 PushSubscription 레코드가 있는지
 * `active`: 권한+브라우저+서버가 모두 OK 인 "정상 동작" 상태
 */
export async function getPushSubscriptionStatus(): Promise<{
  supported: boolean;
  permission: NotificationPermission;
  browserSubscribed: boolean;
  serverActive: boolean;
  active: boolean;
}> {
  if (!isPushSupported()) {
    return {
      supported: false,
      permission: 'default',
      browserSubscribed: false,
      serverActive: false,
      active: false,
    };
  }

  const permission = Notification.permission;
  let browserSubscribed = false;
  let endpoint: string | null = null;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    browserSubscribed = !!subscription;
    endpoint = subscription?.endpoint ?? null;
  } catch (error) {
    console.error('Error checking subscription status:', error);
  }

  let serverActive = false;
  if (permission === 'granted' && browserSubscribed && endpoint) {
    try {
      const res = await api.get('/notifications/push/status', {
        params: { endpoint, device_id: getOrCreateDeviceId() },
      });
      serverActive = !!res.data?.active;
    } catch (error) {
      console.error('Error checking server-side push status:', error);
      serverActive = false;
    }
  }

  return {
    supported: true,
    permission,
    browserSubscribed,
    serverActive,
    active: permission === 'granted' && browserSubscribed && serverActive,
  };
}

/**
 * Initialize push notifications
 * Call this after user login
 */
let subscriptionChangeListenerInstalled = false;

export async function initializePush(): Promise<void> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return;
  }

  // Register service worker
  await registerServiceWorker();

  // SW 가 구독 교체(pushsubscriptionchange)를 알리면 새 구독을 서버에 다시 등록
  if (!subscriptionChangeListenerInstalled) {
    subscriptionChangeListenerInstalled = true;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED' && isNotificationPermissionGranted()) {
        subscribeToPush().catch(console.error);
      }
    });
  }

  // If permission is already granted, subscribe automatically
  if (isNotificationPermissionGranted()) {
    await subscribeToPush();
  }
}
